import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarClock, Check, Clock3, ShieldCheck, Users } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/landing/MissionReadyPublicSite";
import { SchedulingDemo } from "@/components/landing/SchedulingDemo";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD, softwareApplicationJsonLd, SITE_URL } from "@/lib/structured-data";

const TITLE = "Security Guard Scheduling Software | MissionReady";
const DESCRIPTION =
  "Build shifts, assign officers, verify coverage, and deploy security teams with MissionReady scheduling — with compliance checks before every shift.";

export const Route = createFileRoute("/scheduling")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: "Drag, assign, and deploy security officers without wrestling with spreadsheets.",
        url: `${SITE_URL}/scheduling`,
      }),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/scheduling` }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,
      softwareApplicationJsonLd({
        name: "MissionReady Scheduling",
        description: DESCRIPTION,
        url: `${SITE_URL}/scheduling`,
        featureList: [
          "Drag-and-drop shift planning",
          "Officer assignment across sites and posts",
          "Credential and compliance checks before deployment",
          "Live schedule status for dispatch and field teams",
        ],
      }),
    ],
  }),

  component: SchedulingPage,
});

const CAPABILITIES = [
  [CalendarClock, "Shift planning", "Build a clear coverage plan across sites, posts, and time windows."],
  [Users, "Officer assignment", "Assign qualified officers and see open coverage before deployment."],
  [ShieldCheck, "Compliance checks", "Surface credential and assignment warnings before a shift begins."],
  [Clock3, "Live schedule status", "Keep dispatchers and field teams aligned as the operation changes."],
] as const;

function SchedulingPage() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <section className="mr-product-hero"><div className="mr-grid-bg" aria-hidden="true" /><div className="mr-shell"><span className="mr-live-chip"><i /> MISSIONREADY SCHEDULING</span><h1>BUILD THE SHIFT.<br /><em>DEPLOY WITH CONFIDENCE.</em></h1><p>Build schedules, assign officers, and organize coverage without wrestling with spreadsheets.</p><div className="mr-process"><span>DRAG</span><ArrowRight /><span>ASSIGN</span><ArrowRight /><span>DEPLOY</span></div></div></section>
        <section className="mr-story-section is-detailed"><div className="mr-shell mr-story-grid"><div className="mr-story-copy"><span className="mr-interactive-label"><i /> INTERACTIVE SCHEDULER</span><h2>TRY THE SHIFT<br /><em>BUILDER.</em></h2><p>Drag an available officer onto any shift. The production scheduler adds live organization data, permissions, compliance preflight, open shifts, and shift claiming.</p></div><SchedulingDemo detailed /></div></section>
        <section className="mr-capabilities"><div className="mr-shell"><div className="mr-capability-grid">{CAPABILITIES.map(([Icon, title, copy]) => <article key={title}><Icon /><h2>{title}</h2><p>{copy}</p></article>)}</div><div className="mr-product-cta"><h2>READY TO BUILD<br /><em>THE NEXT SHIFT?</em></h2><p>Start with MissionReady, then open the full scheduler from your operations workspace.</p><Link to="/signup" className="mr-button">START FREE <ArrowRight /></Link><span><Check /> 30-day trial · No credit card</span></div></div></section>
      </main>
      <PublicFooter />
    </div>
  );
}